import { Container } from 'pixi.js';
import type { PlayOffer } from '@goodtime/shared';
import type { TitleCardOptions } from '../board/TitleCard';
import { MINI_GAME_SCREENS } from './registry';
import type { MiniGameDeps, MiniGameEnd, MiniGameScreen, MiniGameSnapshot, PlayApi } from './types';

/** What the layer needs beyond what each game needs. */
export interface MiniGameLayerDeps extends MiniGameDeps {
  /** The TV title card; resolves when it has gone. */
  titleCard(text: string, options: TitleCardOptions): Promise<void>;
  /** Fetch a game's art; resolves when it's in (or failed). */
  loadArt(kind: string): Promise<void>;
}

/** The sting under a game's title card. */
const STING = 'road:run_sting';

/**
 * Where the road's mini-games play on the slot canvas: over the reels, the
 * HUD and the road's chrome. Each game's screen is built the first time
 * it's played and kept. A game opens like TV: its title card plays while
 * its art loads, then its board opens.
 */
export class MiniGameLayer extends Container {
  private readonly screens = new Map<string, MiniGameScreen>();
  private active: MiniGameScreen | null = null;
  private viewW = 1;
  private viewH = 1;

  constructor(private readonly deps: MiniGameLayerDeps) {
    super();
  }

  /** Whether this layer plays `kind`. */
  plays(kind: string): boolean {
    return kind in MINI_GAME_SCREENS;
  }

  /** True while a game is on screen. */
  get busy(): boolean {
    return this.active !== null;
  }

  /** Play `offer` to its end. A friend's game is under `title`, its own
   *  name on that road, on its title card and its board; any other game is
   *  under the game's name. */
  async play(offer: PlayOffer, api: PlayApi, title?: string): Promise<MiniGameEnd> {
    const entry = MINI_GAME_SCREENS[offer.kind];
    if (!entry || this.active) return { prize: null };
    let screen = this.screens.get(offer.kind);
    if (!screen) {
      screen = entry.create(this.deps);
      this.screens.set(offer.kind, screen);
      this.addChild(screen);
    }
    this.active = screen;
    const own = entry.titleFor?.(offer) ?? entry;
    const card = title ? { title, sub: own.sub } : own;
    screen.setTitle(card.title);
    try {
      await Promise.all([
        this.deps.titleCard(card.title, { sub: card.sub, sting: STING, kind: 'game' }),
        this.deps.loadArt(offer.kind).catch(() => undefined),
      ]);
      screen.layout(this.viewW, this.viewH);
      return await screen.play(offer, api);
    } finally {
      this.active = null;
    }
  }

  layout(viewWidth: number, viewHeight: number): void {
    this.viewW = viewWidth;
    this.viewH = viewHeight;
    this.screens.forEach((s) => s.layout(viewWidth, viewHeight));
  }

  /** The game on screen, as plain data (browser tests), or null. */
  get snapshot(): MiniGameSnapshot | null {
    return this.active?.snapshot ?? null;
  }

  press(label: string): boolean {
    return this.active?.press(label) ?? false;
  }

  tap(index: number): boolean {
    return this.active?.tap(index) ?? false;
  }
}
